import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaImage, FaUpload } from "react-icons/fa";
import toast from "react-hot-toast";
import adminApi from "services/admin-api";
import {
  SportFormSecondaryButton,
  SportFormPrimaryButton,
  SportFormContentStackTertiary,
  SportFormEmptyStatePanel,
  SportFormActionRow,
  SportFormGridLayout,
  SportFormContentStack,
  SportFormGridLayoutSecondary,
  SportFormContentStackSecondary,
  SportFormHeaderRow,
  SportFormPreviewImage,
  SportFormFormStack,
  SportFormFieldInputSecondary,
  SportFormFieldInput,
  SportFormFieldLabelSecondary,
  SportFormFieldLabel,
  SportFormMetaText,
  SportFormFieldSelect,
  SportFormInlineText,
} from "components/molecules/forms/sport-form/styles/sport-form.inline.styles";
type SportImageValue = string | File | null | undefined;
interface SportFormValues {
  name: string;
  image?: SportImageValue;
  region?: string;
  level?: string;
  status?: string;
  founded_year?: number | string | null;
  popularity_rank?: number | string | null;
}
interface SportFormProps {
  initialValues: SportFormValues;
  onSubmit: (values: SportFormValues) => Promise<void> | void;
  onCancel?: () => void;
}

const earliestFoundedYear = 1;

const resolveImagePreview = (value?: SportImageValue) => (typeof value === "string" ? value : "");
const isInternationalLevel = (value?: string) =>
  String(value || "").trim().toLowerCase() === "international";

const getRegionCountryId = (region: any) =>
  String(region?.country_id || region?.country?.id || region?.country?.country_id || "");

const normalizeRegionName = (value?: string) => String(value || "").trim().toLowerCase();

export const SportForm: React.FC<SportFormProps> = ({ initialValues, onSubmit, onCancel }) => {
  const [values, setValues] = useState<SportFormValues>(initialValues);
  const [previewUrl, setPreviewUrl] = useState(resolveImagePreview(initialValues.image));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const { data: countries = [] } = useQuery({
    queryKey: ["countries", "sport-form"],
    queryFn: () => adminApi.countries.list(),
  });
  const { data: administrativeRegions = [] } = useQuery({
    queryKey: ["administrative-regions", "sport-form"],
    queryFn: () => adminApi.administrativeRegions.list(),
  });

  const countryOptions = useMemo(
    () =>
      [...countries].sort((left: any, right: any) =>
        String(left?.name || "").localeCompare(String(right?.name || "")),
      ),
    [countries],
  );

  const matchedRegion = useMemo(
    () =>
      administrativeRegions.find(
        (region: any) => normalizeRegionName(region?.name) === normalizeRegionName(values.region),
      ) || null,
    [administrativeRegions, values.region],
  );

  const filteredAdministrativeRegions = useMemo(() => {
    if (isInternationalLevel(values.level)) {
      return [];
    }

    if (!selectedCountryId) {
      return [];
    }

    return administrativeRegions.filter(
      (region: any) => getRegionCountryId(region) === String(selectedCountryId),
    );
  }, [administrativeRegions, selectedCountryId, values.level]);

  const regionOptions = useMemo(() => {
    const names: string[] = filteredAdministrativeRegions
      .map((region: any) => region?.name)
      .filter((regionName: string | undefined): regionName is string => Boolean(regionName?.trim()));

    const currentRegion = values.region?.trim();
    const currentRegionCountryId = matchedRegion ? getRegionCountryId(matchedRegion) : "";

    if (
      currentRegion &&
      !names.includes(currentRegion) &&
      (!selectedCountryId ||
        !currentRegionCountryId ||
        currentRegionCountryId === String(selectedCountryId))
    ) {
      names.unshift(currentRegion);
    }

    return Array.from(new Set(names)).sort((regionA: string, regionB: string) =>
      regionA.localeCompare(regionB),
    );
  }, [filteredAdministrativeRegions, matchedRegion, selectedCountryId, values.region]);

  useEffect(() => {
    setValues(initialValues);
    setPreviewUrl(resolveImagePreview(initialValues.image));
    setSelectedCountryId("");
  }, [initialValues]);

  useEffect(() => {
    if (!administrativeRegions.length || selectedCountryId || !initialValues.region?.trim()) {
      return;
    }

    const inferredRegion = administrativeRegions.find(
      (region: any) =>
        normalizeRegionName(region?.name) === normalizeRegionName(initialValues.region),
    );

    setSelectedCountryId(inferredRegion ? getRegionCountryId(inferredRegion) : "");
  }, [administrativeRegions, initialValues.region, selectedCountryId]);

  const updateValue = (key: keyof SportFormValues, value: string | File | null) => {
    setValues((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleCountryChange = (nextCountryId: string) => {
    setSelectedCountryId(nextCountryId);

    const currentRegionMatchesCountry = administrativeRegions.some(
      (region: any) =>
        normalizeRegionName(region?.name) === normalizeRegionName(values.region) &&
        getRegionCountryId(region) === String(nextCountryId),
    );

    if (!currentRegionMatchesCountry) {
      updateValue("region", "");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!values.name?.trim()) {
      toast.error("Sport name is required.");
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...values,
        name: values.name.trim(),
        region: isInternationalLevel(values.level) ? undefined : values.region?.trim() || undefined,
        level: values.level?.trim() || undefined,
        status: values.status || "active",
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Unable to save this sport.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const regionSelectDisabled = isInternationalLevel(values.level) || !selectedCountryId;

  return (
    <SportFormFormStack onSubmit={handleSubmit}>
      <SportFormGridLayout>
        <SportFormContentStack>
          <SportFormFieldLabel htmlFor="sport-form-name">Sport name</SportFormFieldLabel>
          <SportFormFieldInput
            id="sport-form-name"
            onChange={(event) => updateValue("name", event.target.value)}
            placeholder="e.g. Football"
            required
            value={values.name || ""}
          />
        </SportFormContentStack>

        <SportFormGridLayoutSecondary>
          <SportFormContentStack>
            <SportFormFieldLabel htmlFor="sport-form-country">Country</SportFormFieldLabel>
            <SportFormFieldSelect
              id="sport-form-country"
              onChange={(event) => handleCountryChange(event.target.value)}
              value={selectedCountryId}
            >
              <option value="">
                {isInternationalLevel(values.level)
                  ? "Optional for international sports"
                  : "Select country"}
              </option>
              {countryOptions.map((country: any) => (
                <option key={country.id || country.name} value={String(country.id || country.name)}>
                  {country.name}
                </option>
              ))}
            </SportFormFieldSelect>
          </SportFormContentStack>
          <SportFormContentStack>
            <SportFormFieldLabel htmlFor="sport-form-region">Region</SportFormFieldLabel>
            <SportFormFieldSelect
              disabled={regionSelectDisabled}
              id="sport-form-region"
              onChange={(event) => updateValue("region", event.target.value)}
              value={values.region || ""}
            >
              <option value="">
                {isInternationalLevel(values.level)
                  ? "Not required for international sports"
                  : !selectedCountryId
                  ? "Select country first"
                  : regionOptions.length
                  ? "Select region"
                  : "No regions available"}
              </option>
              {regionOptions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </SportFormFieldSelect>
          </SportFormContentStack>
          <SportFormContentStack>
            <SportFormFieldLabel htmlFor="sport-form-level">Level</SportFormFieldLabel>
            <SportFormFieldSelect
              id="sport-form-level"
              onChange={(event) => {
                const nextLevel = event.target.value;
                updateValue("level", nextLevel);

                if (isInternationalLevel(nextLevel)) {
                  setSelectedCountryId("");
                  updateValue("region", "");
                }
              }}
              value={values.level || ""}
            >
              <option value="">Select level</option>
              <option value="Local">Local</option>
              <option value="Provincial">Provincial</option>
              <option value="National">National</option>
              <option value="International">International</option>
            </SportFormFieldSelect>
          </SportFormContentStack>
          <SportFormContentStack>
            <SportFormFieldLabel htmlFor="sport-form-status">Status</SportFormFieldLabel>
            <SportFormFieldSelect
              id="sport-form-status"
              onChange={(event) => updateValue("status", event.target.value)}
              value={values.status || "active"}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </SportFormFieldSelect>
          </SportFormContentStack>
          <SportFormContentStack>
            <SportFormFieldLabel htmlFor="sport-form-founded-year">Founded year</SportFormFieldLabel>
            <SportFormFieldInput
              id="sport-form-founded-year"
              max={new Date().getFullYear()}
              min={earliestFoundedYear}
              onChange={(event) => updateValue("founded_year", event.target.value)}
              placeholder="1744"
              type="number"
              value={values.founded_year ?? ""}
            />
          </SportFormContentStack>
        </SportFormGridLayoutSecondary>

        <SportFormContentStack>
          <SportFormFieldLabel htmlFor="sport-form-popularity-rank">Popularity rank</SportFormFieldLabel>
          <SportFormFieldInput
            id="sport-form-popularity-rank"
            max={10}
            min={1}
            onChange={(event) => updateValue("popularity_rank", event.target.value)}
            placeholder="1 to 10"
            type="number"
            value={values.popularity_rank ?? ""}
          />
        </SportFormContentStack>

        <SportFormContentStackSecondary>
          <SportFormHeaderRow>
            <div>
              <SportFormFieldLabel htmlFor="sport-form-image-url">Sport image</SportFormFieldLabel>
              <SportFormMetaText>
                Choose a local file or paste a hosted image URL.
              </SportFormMetaText>
            </div>
            <SportFormFieldLabelSecondary>
              <FaUpload size={12} />
              Choose file
              <SportFormFieldInputSecondary
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  updateValue("image", file);
                  setPreviewUrl(URL.createObjectURL(file));
                }}
                type="file"
              />
            </SportFormFieldLabelSecondary>
          </SportFormHeaderRow>

          <SportFormContentStackTertiary>
            {previewUrl ? (
              <SportFormPreviewImage
                alt="Sport preview"
                fallbackLabel={values.name || "Sport preview"}
                src={previewUrl}
                variant="sport"
              />
            ) : (
              <SportFormEmptyStatePanel>
                <SportFormInlineText>
                  <FaImage size={14} />
                  No image selected yet
                </SportFormInlineText>
              </SportFormEmptyStatePanel>
            )}

            <SportFormFieldInput
              id="sport-form-image-url"
              onChange={(event) => {
                updateValue("image", event.target.value);
                setPreviewUrl(event.target.value);
              }}
              placeholder="https://example.com/sport-image.jpg"
              value={typeof values.image === "string" ? values.image : ""}
            />
          </SportFormContentStackTertiary>
        </SportFormContentStackSecondary>
      </SportFormGridLayout>

      <SportFormActionRow>
        <SportFormSecondaryButton onClick={onCancel} type="button">
          Cancel
        </SportFormSecondaryButton>
        <SportFormPrimaryButton disabled={isSubmitting} type="submit">
          {isSubmitting ? "Saving..." : initialValues?.name ? "Save changes" : "Add sport"}
        </SportFormPrimaryButton>
      </SportFormActionRow>
    </SportFormFormStack>
  );
};
